import React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { designSystem } from '../../design-system';

const ContactContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundImage: 'url(/texture.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: '100px 80px',

  '@media (max-width: 1600px)': {
    padding: '80px 64px',
  },

  '@media (max-width: 1440px)': {
    padding: '70px 48px',
  },

  '@media (max-width: 1024px)': {
    padding: '60px 40px',
  },

  '@media (max-width: 768px)': {
    padding: '50px 24px',
  },
});

const ContactContent = styled(Box)({
  maxWidth: '1400px',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '80px',
  alignItems: 'center',

  '@media (max-width: 1600px)': {
    maxWidth: '1200px',
    gap: '64px',
  },

  '@media (max-width: 1440px)': {
    maxWidth: '1100px',
    gap: '48px',
  },

  '@media (max-width: 968px)': {
    gridTemplateColumns: '1fr',
    gap: '40px',
  },
});

const LeftSection = styled(motion.div)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const TitleImage = styled('img')({
  width: '100%',
  maxWidth: '600px',
  height: 'auto',
  objectFit: 'contain',

  '@media (max-width: 1600px)': {
    maxWidth: '540px',
  },

  '@media (max-width: 1440px)': {
    maxWidth: '480px',
  },

  '@media (max-width: 968px)': {
    maxWidth: '450px',
  },

  '@media (max-width: 768px)': {
    maxWidth: '380px',
  },
});

const RightSection = styled(motion.div)({
  display: 'flex',
  justifyContent: 'flex-end',

  '@media (max-width: 968px)': {
    justifyContent: 'center',
  },
});

const ContactButton = styled(motion.a)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  padding: '38px 52px',
  backgroundColor: 'transparent',
  border: '1px solid rgba(255, 255, 255, 1)',
  borderRadius: '8px',
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.6, 0.01, 0.05, 0.95)',
  backdropFilter: 'blur(25px)',
  WebkitBackdropFilter: 'blur(25px)',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  },

  '@media (max-width: 1600px)': {
    padding: '34px 46px',
  },

  '@media (max-width: 1440px)': {
    padding: '30px 40px',
    gap: '20px',
  },

  '@media (max-width: 968px)': {
    padding: '28px 36px',
  },

  '@media (max-width: 600px)': {
    padding: '24px 32px',
    gap: '16px',
  },
});

const ButtonText = styled('span')({
  position: 'relative',
  fontFamily: designSystem.typography.fontFamily.ja,
  fontSize: '24px',
  fontWeight: 400,
  color: designSystem.colors.lightText,
  letterSpacing: '0.48px',
  opacity: 0.8,
  lineHeight: 1.4,
  textAlign: 'center',
  whiteSpace: 'nowrap',

  '@media (max-width: 1600px)': {
    fontSize: '22px',
    letterSpacing: '0.44px',
  },

  '@media (max-width: 1440px)': {
    fontSize: '20px',
    letterSpacing: '0.4px',
  },

  '@media (max-width: 968px)': {
    fontSize: '18px',
    letterSpacing: '0.36px',
  },

  '@media (max-width: 600px)': {
    fontSize: '16px',
    letterSpacing: '0.32px',
    whiteSpace: 'normal',
  },
});

const ButtonIcon = styled('img')({
  position: 'relative',
  width: '80px',
  height: '80px',
  objectFit: 'contain',
  flexShrink: 0,

  '@media (max-width: 1600px)': {
    width: '72px',
    height: '72px',
  },

  '@media (max-width: 1440px)': {
    width: '64px',
    height: '64px',
  },

  '@media (max-width: 968px)': {
    width: '56px',
    height: '56px',
  },

  '@media (max-width: 600px)': {
    width: '48px',
    height: '48px',
  },
});

const ContactSection = () => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.3 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.01, 0.05, 0.95],
      },
    },
  };

  return (
    <ContactContainer id="contact" ref={ref}>
      <motion.div
        style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        variants={containerVariants}
        initial="hidden"
        animate={isVisible ? 'visible' : 'hidden'}
      >
        <ContactContent>
          {/* Left Section - Title */}
          <LeftSection variants={itemVariants}>
            <TitleImage src="/title_contact.png" alt="輝くためではなく、照らすために。" />
          </LeftSection>

          {/* Right Section - Contact Button */}
          <RightSection variants={itemVariants}>
            <ContactButton
              href="mailto:masayuki-fukuyoshi@stellajp.net?subject=お問い合わせ"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ButtonText>ご依頼やご相談のお問い合わせ</ButtonText>
              <ButtonIcon src="/icon_circle_right.png" alt="Contact" />
            </ContactButton>
          </RightSection>
        </ContactContent>
      </motion.div>
    </ContactContainer>
  );
};

export default ContactSection;

